/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { ReactElement, useEffect, useRef, useState } from 'react'
import Highcharts from 'highcharts2/highstock'
import i18n from 'live/services/i18n'
import authorization from 'live/services/authorization'
import { RawActivityEvent, RawStaticDataEvent } from '../utils/type'
import {
    ButtonsContainer,
    DeleteConfirmationModalContainer,
    Divisor,
    ErrorNotificationWrapper,
    GlobalNotificationWrapper,
    ModalContent,
    StyledAddEventButton
} from './chart-components-style'
import Modal from '@material-ui/core/Modal'
import { createPortal } from 'react-dom'
import { InputRadioContainer } from './modal-components-style'

// eslint-disable-next-line @typescript-eslint/no-var-requires
require('highcharts/modules/xrange')(Highcharts)

export enum STATIC_DATA_STATES {
    STARTED_IN_EXECUTION = 'STARTED_IN_EXECUTION',
    CREATED_NOT_INITIALIZED = 'CREATED_NOT_INITIALIZED',
    STARTED_WITH_RESTRICTED_D = 'STARTED_WITH_RESTRICTED_D',
    EMPTY = 'EMPTY',
    EMPTY_DISABLED = 'EMPTY_DISABLED',
    FINISHED = 'FINISHED',
    FINISHED_RESTRICTED = 'FINISHED_RESTRICTED',
    FINISHED_WITHOUT_KPI = 'FINISHED_WITHOUT_KPI',
    STARTED_WITHOUT_KPI = 'STARTED_WITHOUT_KPI',
    DELETED = 'DELETED',
    ALERTED = 'ALERTED',
    VALIDATED_GOLDEN_DB = 'VALIDATED_GOLDEN_DB',
    VALIDATED_GOLDEN_DB_RESTRICTED = 'VALIDATED_GOLDEN_DB_RESTRICTED'
}

export enum STATES_COLORS {
    // empty or created
    EMPTY_DISABLED = 'var(--gray-200)',
    EMPTY = 'var(--gray-400)',
    CREATED_NOT_INITIALIZED = 'var(--gray-400)',
    // started
    STARTED_IN_EXECUTION = '#75C3F0',
    STARTED_WITH_RESTRICTED_D = '#75C3F0',
    STARTED_WITHOUT_KPI = '#75C3F0',
    // finished
    FINISHED = '#1865A0',
    FINISHED_RESTRICTED = '#1865A0',
    FINISHED_WITHOUT_KPI = '#1865A0',
    // golden database kpi
    VALIDATED_GOLDEN_DB = '#1EC3BD',
    VALIDATED_GOLDEN_DB_RESTRICTED = '#1EC3BD',

    ALERTED = '#F68E5A',
    DELETED = ''
}

export enum StaticDataType {
    INTERVENTION = 'intervention',
    PHASE = 'section / phase',
    RIG_TABLE = 'rig table status',
    RUN = 'run',
    ACTIVITY = 'tubular type / activity'
}

// for i18n translation
export enum StaticDataTypeI18nKey {
    INTERVENTION = 'INTERVENTION_KEY',
    PHASE = 'PHASE_KEY',
    RIG_TABLE = 'RIG_TABLE_KEY',
    RUN = 'RUN_KEY',
    ACTIVITY = 'ACTIVITY_KEY'
}

export enum UnitsTypes {
    LENGTH = 'LENGTH',
    TIME = 'TIME'
}
interface AddEventButtonProps {
    visible: boolean
    openModal: (rawStaticDataEvent: RawStaticDataEvent) => void
}

export const AddEventButton = ({ visible, openModal }: AddEventButtonProps): ReactElement => (
    <StyledAddEventButton visible={visible} onClick={() => openModal(null)}>
        <i className="fas fa-plus" />
        <strong>{i18n('create event').toUpperCase()}</strong>
    </StyledAddEventButton>
)

export const renderButtons = (
    buttonsRef: React.RefObject<HTMLInputElement>,
    currentChart: any,
    buttonProps: { buttonFunctions: (() => void)[]; customIcons?: string[] | null }
): ReactElement => {
    const hasPermissionToDelete =
        authorization.superAdmin ||
        authorization.admin ||
        authorization.Permission.ADMIN ||
        authorization.Permission.OGPERFORMANCE_MANAGE_KPI ||
        authorization.Permission.OGPERFORMANCE_ADMINISTRATE_KPI

    let LEFT_POSITION, TOP_POSITION
    if (currentChart) {
        if (!currentChart.tooltipPos || !currentChart.graphic.element) {
            return
        }
        LEFT_POSITION =
            currentChart.graphic.element.getBoundingClientRect().left +
            currentChart.graphic.element.getBoundingClientRect().width / 2
        TOP_POSITION = currentChart.graphic.element.getBoundingClientRect().bottom + 5
    }

    const tooltipStatus: any[] = []
    if (currentChart?.rawEvent) {
        const eventChart = currentChart?.rawEvent

        // INTERVENTION
        if (eventChart.rig_id) {
            tooltipStatus.push(
                <span>
                    {i18n('Intervention name')}: <strong>{eventChart.name ?? '-'}</strong>
                </span>
            )
        }

        // SECTION AND RIG TABLE
        if (eventChart.intervention_id) {
            if (!eventChart.oD?.value) {
                tooltipStatus.push(
                    <span>
                        {i18n('Rig Table Path Status')}: <strong>{eventChart.status ?? '-'}</strong>
                    </span>
                )
            } else {
                tooltipStatus.push(
                    <span>
                        {i18n('Section OD')}:{' '}
                        <strong>{`${eventChart.oD?.value ?? '-'} ${eventChart.oD?.uom?.name ?? '-'}`}</strong>
                    </span>
                )
            }
        }

        // RUN
        if (eventChart.section_id) {
            const explodedRunType = eventChart.type.toLowerCase().split('_')

            let formattedRunType = ''
            // Capitalizing the first letter of each word in a string
            for (let i = 0; i < explodedRunType.length; i++) {
                explodedRunType[i] = explodedRunType[i].charAt(0).toUpperCase() + explodedRunType[i].slice(1)
            }

            formattedRunType = explodedRunType.toString().replaceAll(',', ' ')

            tooltipStatus.push(
                <>
                    <span>
                        {i18n('Run Number')}: <strong>{eventChart.number ?? '-'}</strong>
                    </span>
                    <span>
                        {i18n('Run Type')}:{' '}
                        <strong>{formattedRunType === 'Packerrun' ? 'Packer Run' : formattedRunType ?? '-'}</strong>
                    </span>
                </>
            )
        }

        // TUBULAR
        if (eventChart.run_id) {
            const isTubularTypeWithRunningOrTubing = eventChart.tubularType.includes('RUNNING', 'HANDLING')

            // The words 'RUNNING' and 'HANDLING' will always be in the last position of the types that contain these words.
            let formattedTubularType = ''
            if (isTubularTypeWithRunningOrTubing) {
                const explodedTubularType = eventChart.tubularType.toLowerCase().split('_')

                // Capitalizing the first letter of each word in a string
                for (let i = 0; i < explodedTubularType.length; i++) {
                    explodedTubularType[i] =
                        explodedTubularType[i].charAt(0).toUpperCase() + explodedTubularType[i].slice(1)
                }

                formattedTubularType = explodedTubularType
                    .slice(0, explodedTubularType.length - 1)
                    .toString()
                    .replaceAll(',', ' ')
            } else {
                formattedTubularType = eventChart.tubularType.replaceAll('_', ' ')
            }

            tooltipStatus.push(
                <span>
                    {i18n('Tubular Type')}: <strong>{formattedTubularType ?? '-'}</strong>
                </span>
            )
            ;(eventChart.tubularType === 'CASING_RUNNING' || eventChart.tubularType === 'DPR_RUNNING') &&
                tooltipStatus.push(
                    <span>
                        {i18n('Tubular OD')}:{' '}
                        <strong>{`${eventChart.oD?.value ?? '-'} ${eventChart.oD?.uom?.name ?? '-'}`}</strong>
                    </span>
                )
        }
    }

    return createPortal(
        <ButtonsContainer
            ref={buttonsRef}
            id={currentChart?.id}
            className="custom-buttons-container"
            tootipLeftPosition={LEFT_POSITION}
            tooltipTopPosition={TOP_POSITION - 9}
        >
            <i className="fas fa-caret-up toogler"></i>

            {buttonProps.customIcons ? (
                <div className="btn-wrapper">
                    {tooltipStatus.length > 0 && (
                        <>
                            {tooltipStatus}
                            <Divisor />
                        </>
                    )}
                    <div className="btn-content">
                        {buttonProps.customIcons.map((icon, index) => {
                            if (!hasPermissionToDelete) {
                                if (icon !== 'far fa-trash-alt') {
                                    return (
                                        <i key={icon} className={icon} onClick={buttonProps.buttonFunctions[index]}></i>
                                    )
                                }
                            } else {
                                return <i key={icon} className={icon} onClick={buttonProps.buttonFunctions[index]}></i>
                            }
                        })}
                    </div>
                </div>
            ) : (
                <div className="btn-wrapper">
                    <div className="btn-content">
                        {hasPermissionToDelete && (
                            <i className="far fa-trash-alt" onClick={buttonProps.buttonFunctions[0]}></i>
                        )}
                        <i className="far fa-eye" onClick={buttonProps.buttonFunctions[1]}></i>
                        <i className="far fa-calendar-check" onClick={buttonProps.buttonFunctions[1]}></i>
                    </div>
                </div>
            )}
        </ButtonsContainer>,
        document.body
    )
}

export const renderCreationTooltip = (
    buttonsRef: React.RefObject<HTMLInputElement>,
    currentChart: any,
    tooltipPosition: { left?: number; top?: number },
    buttonController: boolean | STATIC_DATA_STATES,
    message: { date: string; hour: string; text: string; fatherType?: string; parentStart?: string }
): ReactElement | void => {
    let LEFT_POSITION, TOP_POSITION, PIN_LEFT_POSITION, PIN_RIGHT_POSITION

    if (!message) return
    if (currentChart) {
        if (!currentChart.tooltipPos || !currentChart.graphic?.element) return

        const trackClientRect = currentChart.graphic?.parentGroup?.element?.getBoundingClientRect()
        const halfOfButtonWidth = (buttonsRef?.current?.getBoundingClientRect()?.width ?? 150) / 2

        TOP_POSITION = currentChart.graphic.element.getBoundingClientRect().bottom + 2
        PIN_RIGHT_POSITION = 'initial'

        const lowerBound = trackClientRect?.left + halfOfButtonWidth + 1.5 - 15
        const upperBound = trackClientRect?.right - halfOfButtonWidth - 1.5 + 15

        if (tooltipPosition.left) {
            if (tooltipPosition.left < lowerBound) {
                LEFT_POSITION = lowerBound - halfOfButtonWidth
            } else if (upperBound < tooltipPosition.left) {
                LEFT_POSITION = upperBound - halfOfButtonWidth
            } else {
                LEFT_POSITION = tooltipPosition.left - halfOfButtonWidth
            }
            PIN_LEFT_POSITION = `${tooltipPosition.left - LEFT_POSITION - 2.5}px`
        }
    }

    if (buttonController) {
        return createPortal(
            <div>
                <div
                    className="chart__track"
                    style={{
                        position: 'absolute',
                        width: '2px',
                        height: `9px`,
                        background: '#37A3EF',
                        zIndex: 9999,
                        left: `${tooltipPosition?.left}px`,
                        top: `${Number(TOP_POSITION) - 21}px`,
                        pointerEvents: 'none'
                    }}
                ></div>
                <ButtonsContainer
                    ref={buttonsRef}
                    id={currentChart?.id}
                    className="create-tooltip"
                    tootipLeftPosition={LEFT_POSITION}
                    tooltipTopPosition={TOP_POSITION}
                    pinLeftPosition={PIN_LEFT_POSITION}
                    pinRightPosition={PIN_RIGHT_POSITION}
                >
                    <div className="tooltip-first-infos">
                        <p>
                            {message.date} . <span id="hour">{message.hour}</span>
                        </p>
                        <p>{message.text}</p>
                    </div>

                    {message.parentStart ? (
                        <div className="tooltip-father-infos">
                            <p className="tooltip-father-type">{`${i18n('Started')} ${message.fatherType}`}</p>

                            <div className="tooltip-father-dates">
                                <p>{message.parentStart}</p>
                            </div>
                        </div>
                    ) : null}
                </ButtonsContainer>
            </div>,
            document.body
        )
    }
}

interface DeleteConfirmationModalProps {
    isOpen: boolean
    closeModal: () => void
    confirmDeletion: (isDeleteMany: boolean) => void
    isActivityModal?: boolean
    isTubularPerTime?: boolean
    event?: RawActivityEvent
}

export const DeleteConfirmationModal = ({
    isOpen,
    closeModal,
    confirmDeletion,
    isActivityModal,
    isTubularPerTime,
    event
}: DeleteConfirmationModalProps): ReactElement => {
    const [isDeleteMany, setIsDeleteMany] = useState(false)
    const handleCloseModal = () => {
        setIsDeleteMany(false)
        closeModal()
    }

    return (
        <Modal open={isOpen} disableBackdropClick>
            <DeleteConfirmationModalContainer>
                <ModalContent>
                    <header>
                        <div className="icon-txt">
                            <i className="fas fa-exclamation-circle" />
                            <span>{i18n('Attention!')}</span>
                        </div>
                        <i className="far fa-times close-btn" onClick={handleCloseModal} />
                    </header>
                    <main>
                        {isActivityModal && !isTubularPerTime ? (
                            <div className="delete-option-content">
                                <div className="delete-options">
                                    <input
                                        type="radio"
                                        id="delete-last-tubular"
                                        name="delete-tubular"
                                        checked={!isDeleteMany}
                                        onChange={e => {
                                            e.persist()
                                            setIsDeleteMany(e.target?.value !== 'on')
                                        }}
                                    />
                                    <label htmlFor="delete-last-tubular">{i18n('Delete only the last tubular.')}</label>
                                </div>
                                <div className="delete-options">
                                    <input
                                        type="radio"
                                        id="delete-all-tubulars-set"
                                        name="delete-tubular"
                                        checked={isDeleteMany}
                                        onChange={e => {
                                            e.persist()
                                            setIsDeleteMany(e.target?.value === 'on')
                                        }}
                                    />
                                    <label htmlFor="delete-all-tubulars-set">
                                        {i18n('Delete the tubular set (Run Workstring).')}
                                    </label>
                                </div>
                                <div className="warning-txt">
                                    <span>{i18n('Are you sure you want to delete?')}</span>
                                    <strong>{i18n('This action cannot be undone.')}</strong>
                                </div>
                            </div>
                        ) : (
                            <div className="warning-txt">
                                <span>
                                    {i18n('Are you sure you want to delete?')}
                                    {!isActivityModal && (
                                        <>
                                            <br /> {i18n('All child events will be deleted along with this one.')}
                                        </>
                                    )}
                                </span>
                                <strong>{i18n('This action cannot be undone.')}</strong>
                            </div>
                        )}
                    </main>
                    <footer>
                        <button className="btn-confirm" onClick={() => confirmDeletion(isDeleteMany)}>
                            {i18n('Confirm deletion')}
                        </button>
                        <button className="btn-cancel" onClick={handleCloseModal}>
                            {i18n('Cancel')}
                        </button>
                    </footer>
                </ModalContent>
            </DeleteConfirmationModalContainer>
        </Modal>
    )
}

interface DeleteConfirmationTubularModalProps {
    isOpen: boolean
    closeModal: () => void
    confirmDeletion: (isDelete: boolean) => void
    isActivityModal?: boolean
}

export const DeleteConfirmationTubularModal = ({
    isOpen,
    closeModal,
    confirmDeletion,
    isActivityModal
}: DeleteConfirmationTubularModalProps): ReactElement => {
    const [isDelete, setIsDelete] = useState(false)
    const handleCloseModal = () => {
        setIsDelete(false)
        closeModal()
    }

    return (
        <Modal open={isOpen} disableBackdropClick>
            <DeleteConfirmationModalContainer>
                <ModalContent>
                    <header>
                        <div className="icon-txt">
                            <i className="fas fa-exclamation-circle" />
                            <span>{i18n('Attention!')}</span>
                        </div>
                        <i className="far fa-times close-btn" onClick={handleCloseModal} />
                    </header>
                    <main>
                        <div className="warning-txt">
                            <span>
                                {i18n(
                                    'Tubular will be finished with changes applied to tubular type and/or restricted performance.'
                                )}
                                {!isActivityModal && (
                                    <>
                                        <br /> {i18n('Are you sure you want to update and finish the tubular event?')}
                                    </>
                                )}
                            </span>
                            <strong>{i18n('This action cannot be undone.')}</strong>
                        </div>
                    </main>
                    <footer>
                        <button className="btn-confirm-changes" onClick={() => confirmDeletion(isDelete)}>
                            {i18n('Confirm changes')}
                        </button>
                        <button className="btn-cancel" onClick={handleCloseModal}>
                            {i18n('Cancel')}
                        </button>
                    </footer>
                </ModalContent>
            </DeleteConfirmationModalContainer>
        </Modal>
    )
}

interface ErrorNotificationProps {
    showMessageAboutStartAnEvent?: boolean
}

export const ErrorNotification = ({ showMessageAboutStartAnEvent }: ErrorNotificationProps): ReactElement => {
    const NotificationErrorRef = useRef(null)

    useEffect(() => {
        const closeBtn = NotificationErrorRef?.current?.nextElementSibling
        if (closeBtn) closeBtn.style.top = '16px'
    }, [NotificationErrorRef.current])

    return (
        <ErrorNotificationWrapper ref={NotificationErrorRef}>
            <i className="fas fa-exclamation-triangle"></i>
            <div className="track-disabled-wrapper">
                {showMessageAboutStartAnEvent ? (
                    <>
                        <p>{i18n('Track disabled')}</p>
                        <p>{i18n('You need to start an event on the tospan layer.')}</p>
                    </>
                ) : (
                    <>
                        <p>{i18n('Track disabled')}</p>
                        <p>
                            {i18n(
                                "You don't have permission to create an event. Please contact your system administrator."
                            )}
                        </p>
                    </>
                )}
            </div>
        </ErrorNotificationWrapper>
    )
}

export type GlobalNotificationProps = {
    title: string
    description?: string
    type?: 'success' | 'warn' | 'error'
}

export const GlobalNotification = ({ title, description, type = 'error' }: GlobalNotificationProps): ReactElement => {
    const GlobalNotificationRef = useRef<null | HTMLDivElement>(null)

    useEffect(() => {
        const closeBtn = GlobalNotificationRef?.current?.nextElementSibling
        if (closeBtn) (closeBtn as HTMLAnchorElement).style.top = '16px'
    }, [GlobalNotificationRef.current])

    return (
        <GlobalNotificationWrapper ref={GlobalNotificationRef}>
            {type === 'success' ? <i className="far fa-check-circle" /> : <i className="far fa-exclamation-triangle" />}
            <div className="notification-content-wrapper">
                <p>{i18n(title)}</p>
                <p>{i18n(description)}</p>
            </div>
        </GlobalNotificationWrapper>
    )
}

export enum ButtonOptions {
    Delete = 'far fa-trash-alt',
    Watch = 'far fa-eye',
    Add = 'far fa-calendar-plus',
    Confirm = 'far fa-calendar-check'
}

export interface InputRadioProps extends React.InputHTMLAttributes<HTMLInputElement> {
    labelLeft?: string
    labelRight?: string
    showRadioWhenDisabled?: boolean
}

export const InputRadio = (props: InputRadioProps): JSX.Element => {
    return (
        <InputRadioContainer showRadioWhenDisabled={props?.showRadioWhenDisabled}>
            {props?.labelLeft && <label style={{ marginRight: '4px' }}>{props.labelLeft}</label>}
            <input type="radio" {...props} />
            {props?.labelRight && <label style={{ marginLeft: '4px' }}>{props.labelRight}</label>}
        </InputRadioContainer>
    )
}
